import DataTable, { TableColumn } from 'react-data-table-component'
import { Card } from '@radix-ui/themes'

import { getOtherCost, getTotalMargin } from '../../utils/margin-report-helpers'

import { fechaIsoToYYYY_MM_DD, formatDate, formatWithThousandSep } from '@/utils/utils'
import Margin from '@/interfaces/Margin'

const paginationComponentOptions = {
  rowsPerPageText: 'Filas por página',
  selectAllRowsItemText: 'Todos',
  rangeSeparatorText: 'de',
  selectAllRowsItem: true,
}

const dateSort = ({ date: a }: Margin, { date: b }: Margin) => Date.parse(a) - Date.parse(b)
const numericSort = (a: number, b: number) => a - b

// Ancho mínimo para columnas de montos: evita que react-data-table-component corte los valores con "…"
// (p. ej. "$1.800.0…"). Alcanza para montos de hasta 9 dígitos con signo.
const MONEY_MIN_WIDTH = '9rem'

export const MarginDataTableCard = ({ margins, withAdditionals }: { margins: Margin[]; withAdditionals: boolean }) => {
  const columns: TableColumn<Margin>[] = [
    {
      name: <div>Nota de venta</div>,
      selector: (row) => row.number,
      sortable: true,
    },
    {
      name: <div>Cliente</div>,
      selector: (row) => `${row.clientFirstname} ${row.clientLastname}`,
      sortable: true,
      grow: 2,
    },
    {
      name: <div>RUT</div>,
      selector: (row) => row.clientNin,
      sortable: true,
      grow: 2,
    },
    {
      name: <div>Email</div>,
      selector: (row) => row.clientEmail,
      sortable: true,
      grow: 3,
    },
    {
      name: <div>Teléfono</div>,
      selector: (row) => row.clientPhone,
      sortable: true,
      grow: 2,
    },
    {
      name: <div>Sucursal</div>,
      selector: (row) => row.branch_name || '-',
      sortable: true,
    },
    {
      name: <div>Vendedor</div>,
      selector: (row) => `${row.firstname} ${row.lastname}`,
      sortable: true,
      grow: 3,
    },
    {
      name: <div>Fecha</div>,
      selector: (row) => formatDate(fechaIsoToYYYY_MM_DD(row.date)),
      sortFunction: dateSort,
      sortable: true,
      minWidth: '7rem',
    },
    {
      name: <div>Patente</div>,
      selector: (row) => row.plate,
      sortable: true,
    },
    {
      name: <div>Marca</div>,
      selector: (row: Margin) => row.brand,
      sortable: true,
    },
    {
      name: <div>Modelo y versión</div>,
      selector: (row: Margin) => `${row.model} ${row.version ?? ''}`,
      sortable: true,
      grow: 3,
    },
    {
      name: <div>Color</div>,
      selector: (row: Margin) => row.color ?? '',
      sortable: true,
    },

    {
      name: <div>Año</div>,
      selector: (row: Margin) => row.year,
      sortable: true,
    },
    {
      name: <div>Gastos</div>,
      selector: (row: Margin) => '$' + formatWithThousandSep(row.expensesTotal),
      sortFunction: (a, b) => numericSort(a.expensesTotal, b.expensesTotal),
      sortable: true,
      right: true,
      minWidth: MONEY_MIN_WIDTH,
    },
    {
      name: <div>Compra</div>,
      selector: (row: Margin) => '$' + formatWithThousandSep(row.finalCost),
      sortFunction: (a, b) => numericSort(a.purchaseTotal, b.purchaseTotal),
      sortable: true,
      right: true,
      minWidth: MONEY_MIN_WIDTH,
    },
    {
      name: <div>Parte de pago</div>,
      selector: (row: Margin) => row.tradeInsCount,
      sortable: true,
      right: true,
    },
    {
      name: <div>Parte de pago</div>,
      sortFunction: (a, b) => numericSort(a.tradeInsPriceTotal, b.tradeInsPriceTotal),
      selector: (row: Margin) => '$' + formatWithThousandSep(row.tradeInsPriceTotal),
      sortable: true,
      right: true,
      minWidth: MONEY_MIN_WIDTH,
    },
    {
      name: <div>Patentes parte de pago</div>,
      selector: (row: Margin) => row.tradeInsPlates,
      sortable: true,
      grow: 2,
    },
    {
      name: <div>Liquidación</div>,
      selector: (row: Margin) => '$' + formatWithThousandSep(row.settlementTotal),
      sortFunction: (a, b) => numericSort(a.settlementTotal, b.settlementTotal),
      sortable: true,
      right: true,
      minWidth: MONEY_MIN_WIDTH,
    },

    {
      name: <div>Transferencia</div>,
      sortFunction: (a, b) => numericSort(a.transferOwnerCost, b.transferOwnerCost),
      selector: (row) => '$' + formatWithThousandSep(row.transferOwnerCost),
      omit: !withAdditionals,
      sortable: true,
      right: true,
      minWidth: MONEY_MIN_WIDTH,
    },
    {
      name: <div>Tag</div>,
      selector: (row) => '$' + formatWithThousandSep(row.tagCost),
      sortFunction: (a, b) => numericSort(a.tagCost, b.tagCost),
      omit: !withAdditionals,
      sortable: true,
      right: true,
      minWidth: MONEY_MIN_WIDTH,
    },
    {
      name: <div>Otros adicionales</div>,
      sortFunction: (a, b) => numericSort(getOtherCost(a), getOtherCost(b)),
      selector: (row) => '$' + formatWithThousandSep(getOtherCost(row)),
      omit: !withAdditionals,
      sortable: true,
      right: true,
      minWidth: MONEY_MIN_WIDTH,
    },
    {
      name: <div>Valor venta</div>,
      sortFunction: (a, b) => numericSort(a.salenoteTotal, b.salenoteTotal),
      selector: (row: Margin) => '$' + formatWithThousandSep(row.price),
      sortable: true,
      right: true,
      minWidth: MONEY_MIN_WIDTH,
    },
    {
      name: <div>Descuento</div>,
      selector: (row: Margin) => '$' + formatWithThousandSep(row.discountAmount || 0),
      sortFunction: (a, b) => numericSort(a.discountAmount || 0, b.discountAmount || 0),
      sortable: true,
      right: true,
      minWidth: MONEY_MIN_WIDTH,
    },
    {
      name: <div>Otros ingresos</div>,
      selector: (row: Margin) => '$' + formatWithThousandSep(row.incomesTotal),
      sortFunction: (a, b) => numericSort(a.incomesTotal, b.incomesTotal),
      sortable: true,
      right: true,
      minWidth: MONEY_MIN_WIDTH,
    },
    {
      name: <div>Seguro</div>,
      selector: (row) => row.insuranceCo,
      sortable: true,
    },
    {
      name: <div>Margen Vehículo</div>,
      selector: (row) => '$' + formatWithThousandSep(row.margin),
      sortFunction: (a, b) => numericSort(a.margin, b.margin),
      sortable: true,
      right: true,
      minWidth: MONEY_MIN_WIDTH,
    },
    {
      name: <div>Comisión de Crédito</div>,
      selector: (row: Margin) => '$' + formatWithThousandSep(row.creditFee),
      sortFunction: (a, b) => numericSort(a.creditFee, b.creditFee),
      sortable: true,
      right: true,
      minWidth: MONEY_MIN_WIDTH,
    },
    {
      name: <div>Financiera</div>,
      selector: (row: Margin) => row.creditInstitution || '-',
      sortable: true,
    },
    {
      name: <div>Margen Total</div>,
      selector: (row) => '$' + formatWithThousandSep(getTotalMargin(row)),
      sortFunction: (a, b) => numericSort(getTotalMargin(a), getTotalMargin(b)),
      sortable: true,
      right: true,
      minWidth: MONEY_MIN_WIDTH,
    },
  ]

  return (
    <Card>
      <DataTable
        paginationComponentOptions={paginationComponentOptions}
        noDataComponent="[No hay datos]"
        columns={columns}
        data={margins}
        pagination
        striped
        dense
      />
    </Card>
  )
}
